import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone"; 
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
  Typography,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import Refresh from "@mui/icons-material/Refresh";
import { getAllInventory, updateInventory } from "../../../services/inventoryService.js";
import { getLabTestByIdFromBaseTable, getAllLabTests } from "../../../services/labtestService.js";
import { getBOMsFromBaseTable, updateBOMInBaseTable, getSampleCollectionsFromBaseTable } from "../../../services/labService.js";
// Inventory cell-edit helpers kept for future restore:
// import {
//   getPathItemDisplayName,
//   validateUniqueCatalogName,
// } from "../../../utils/duplicateNameUtils";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Header from "../../../components/Header.jsx";
import CreateNewPathItem from "./CreateNewPathItem.jsx";
import AddNewPurchaseInvoice from "./AddNewPurchaseInvoice.jsx";
import CreateNewLabTest from "./CreateNewLabTest.jsx";
import PathOCRDialog from "./PathOCRDialog.jsx";
import PathOCRResultDialog from "./PathOCRResultDialog.jsx";
import dayjs from "dayjs";
import { useTheme } from "@emotion/react";
import { useAuth } from "../../../context/AuthContext";
import { getOrganizationId, filterByOrganization, getOrganizationName } from "../../../utils/organizationUtils";
import { getInvoiceList } from "../../../services/invoiceService";
import {
  downloadPathInventoryTableExcel,
  downloadStyledTableExcel,
} from "../../../utils/patientTableExcelExport";
import { buildPathInventoryExportTable } from "../../../utils/inventoryBatchExportUtils";
import {
  buildPathInventoryExportSummary,
  buildPathInventoryTransactionsFromRecords,
} from "../../../utils/pathInventorySummary";
import SupplierDetailsTable from "../Product/SupplierDetailsTable";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import {
  isPathModuleStaffRole,
  PATH_INVENTORY_CAPABILITY,
  PATHOLOGY_INVENTORY_LABEL,
} from "../../../utils/pathRoleUtils";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// import SaveIcon from "@mui/icons-material/Save";
// import CancelIcon from "@mui/icons-material/Cancel";
import PathExpiryThresholdSettings from "../../../components/PathExpiryThresholdSettings.jsx";
import PathTransactionHistory from "./PathTransactionHistory.jsx";
import { usePathExpiryThreshold } from "../../../hooks/usePathExpiryThreshold";
import {
  applyExpiryFlagsToGroupedProducts,
  isExpiredExpiryDate,
  readPathExpiryThresholdMonthsFromStorage,
} from "../../../utils/pathExpiryThreshold";
import { readPathLowStockQuantityThreshold, resolvePathCriticalLevel } from "../../../utils/pathLowStock";
import {
  getGroupInventoryBadgeKeys,
  getInventoryNameColor,
  getStockStatus,
  InventoryStatusBadges,
  InventoryStatusLegend,
} from "../../../components/InventoryStatusBadges";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../../utils/pageLayoutStyles";

const columns = [
  { id: "expand", label: "", width: 50 },
  { id: "id", label: "Inventory Id" },
  { id: "name", label: "Name" },
  { id: "price", label: "Price" },
  { id: "quantity", label: "Total Quantity" },
  { id: "criticalLevel", label: "Critical Level" },
  { id: "action", label: "Action", width: 100 },
];

const normalizePathMatchValue = (value) => String(value || "").trim().toLowerCase();

const pathGroupHasBatch = (group, batchNumber) => {
  const batch = normalizePathMatchValue(batchNumber);
  if (!batch) return false;
  return (group?.records || []).some(
    (record) => normalizePathMatchValue(record?.batchNumber) === batch
  );
};

const findPathInventoryGroupForTransaction = (
  productList,
  { itemName, batchNumber, inventoryId, bomId } = {}
) => {
  const list = Array.isArray(productList) ? productList : [];
  const invId =
    inventoryId != null && inventoryId !== "" ? String(inventoryId) : "";
  const catalogBomId = bomId != null && bomId !== "" ? String(bomId) : "";
  const name = normalizePathMatchValue(itemName);

  if (invId) {
    const matched = list.find((group) =>
      (group.records || []).some((record) => String(record?.id) === invId)
    );
    if (matched) return matched;
  }

  if (name && batchNumber) {
    const matched = list.find(
      (group) =>
        normalizePathMatchValue(group?.name) === name &&
        pathGroupHasBatch(group, batchNumber)
    );
    if (matched) return matched;
  }

  if (name) {
    const exact = list.find((group) => normalizePathMatchValue(group?.name) === name);
    if (exact) return exact;
  }

  if (catalogBomId) {
    const matched = list.find(
      (group) =>
        String(group?.bomId) === catalogBomId ||
        (group?.records || []).some((record) => String(record?.bomId) === catalogBomId)
    );
    if (matched) return matched;
  }

  if (batchNumber) {
    const matched = list.find((group) => pathGroupHasBatch(group, batchNumber));
    if (matched) return matched;
  }

  return null;
};

const parseLabTestSampleEntries = (labTest = {}) => {
  if (Array.isArray(labTest.sampleEntries) && labTest.sampleEntries.length) {
    return labTest.sampleEntries
      .map((se) => ({
        sample_id: Number(se.sample_id ?? se.sampleId ?? se.id),
        requiredQuantity:
          se.requiredQuantity ??
          se.required_quantity ??
          se.requiredquantity ??
          "",
        name: se.name || se.sampleName || se.sample_name || "",
        unitType: se.unitType || se.unit_type || se.qtyUnit || "",
      }))
      .filter((entry) => entry.sample_id);
  }
  if (labTest.sampleId) {
    return String(labTest.sampleId)
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .map((id) => ({
        sample_id: Number(id),
        requiredQuantity: "",
        name: "",
        unitType: "",
      }))
      .filter((entry) => entry.sample_id);
  }
  return [];
};

const parseLabTestConsumableEntries = (labTest = {}) => {
  if (Array.isArray(labTest.bomEntries) && labTest.bomEntries.length) {
    return labTest.bomEntries
      .map((be) => ({
        bom_id: Number(be.bom_id ?? be.bomId ?? be.id),
        requiredQuantity:
          be.requiredQuantity ??
          be.required_quantity ??
          be.requiredquantity ??
          "",
        name: be.name || be.itemName || be.bomName || "",
        unitType: be.unitType || be.unit_type || be.qtyUnit || "",
      }))
      .filter((entry) => entry.bom_id);
  }
  if (labTest.bomId != null && String(labTest.bomId).trim() !== "") {
    return String(labTest.bomId)
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .map((id) => ({
        bom_id: Number(id),
        requiredQuantity: "",
        name: "",
        unitType: "",
      }))
      .filter((entry) => entry.bom_id);
  }
  return [];
};

const resolveLabTestSampleDetails = (labTest, samplesById = new Map()) =>
  parseLabTestSampleEntries(labTest).map((entry) => {
    const sample = samplesById.get(Number(entry.sample_id));
    return {
      id: entry.sample_id,
      name: entry.name || sample?.name || `Sample #${entry.sample_id}`,
      unitType:
        entry.unitType || sample?.unitType || sample?.unit_type || sample?.qtyUnit || "",
      requiredQuantity: entry.requiredQuantity,
    };
  });

const resolveLabTestConsumableDetails = (labTest, bomsById = new Map()) =>
  parseLabTestConsumableEntries(labTest).map((entry) => {
    const bom = bomsById.get(Number(entry.bom_id));
    return {
      id: entry.bom_id,
      name: entry.name || bom?.name || `Item #${entry.bom_id}`,
      unitType:
        entry.unitType || bom?.unitType || bom?.unit_type || bom?.qtyUnit || "",
      requiredQuantity: entry.requiredQuantity,
    };
  });

const formatLabTestLinkLine = (row) => {
  const qty =
    row.requiredQuantity != null && row.requiredQuantity !== ""
      ? ` (${row.requiredQuantity}${row.unitType ? ` ${row.unitType}` : ""})`
      : "";
  return `${row.name || "-"}${qty}`;
};

const formatLabTestLinkSummary = (rows = []) =>
  rows.map(formatLabTestLinkLine).filter(Boolean).join("; ");

const LabTestExpandedDetails = ({ samples, consumables, loading }) => {
  const nestedHeadSx = {
    backgroundColor: "#e8eefc",
    color: "#1f3b73",
    fontWeight: 600,
    py: 0.75,
  };
  const renderNestedTable = (title, rows, emptyLabel) => (
    <Box sx={{ mb: title === "Sample Details" ? 1.5 : 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
        {title}
      </Typography>
      {!rows.length ? (
        <Alert severity="info">{emptyLabel}</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={nestedHeadSx}>Name</TableCell>
              <TableCell sx={nestedHeadSx}>Unit Type</TableCell>
              <TableCell sx={nestedHeadSx}>Required Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${title}-${row.id}`}>
                <TableCell>{row.name || "-"}</TableCell>
                <TableCell>{row.unitType || "-"}</TableCell>
                <TableCell>
                  {row.requiredQuantity != null && row.requiredQuantity !== ""
                    ? row.requiredQuantity
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {renderNestedTable("Sample Details", samples, "No sample details")}
      {renderNestedTable(
        "Consumables Details",
        consumables,
        "No consumable details"
      )}
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

export default function PurchaseInvoice({ hideMainTitle = false }) {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { hasCapability } = useSubscriptionCapabilities();
  const hasPathInventoryCapability = hasCapability(PATH_INVENTORY_CAPABILITY);
  const normalizedRole = (user?.roleName || "").toLowerCase();
  const showAdminPathTabs =
    (normalizedRole === "admin" ||
      normalizedRole === "superadmin" ||
      normalizedRole === "super_admin" ||
      isPathModuleStaffRole(normalizedRole)) &&
    hasPathInventoryCapability;
  const {
    getExpiryStatus,
    months: expiryThresholdMonths,
    loading: expiryThresholdLoading,
  } = usePathExpiryThreshold();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ product_list: true, lab_tests: true });
  const [product_list, setProductList] = useState([]);
  const [lab_tests, setLabTests] = useState([]);

  const [open_product_modal, setOpenProductModal] = useState(false);
  const [open_Lab_test_modal, setOpenLabTestModal] = useState(false);
  const [activeTab, setActiveTab] = useState('inventory');
  const [open_bom_modal, setOpenBomModal] = useState(false);
  const [editingBom, setEditingBom] = useState(null);
  const [bomForm, setBomForm] = useState({ name: "", description: "", unitType: "", requiredQuantity: "", cost: "", costPerUnit: "" });
  const [bomList, setBomList] = useState([]);
  const [product_info, setProductInfo] = useState(null);
  const [searchQueries, setSearchQueries] = useState({ inventory: "", items: "", lab: "" });
  const [filterNames, setFilterNames] = useState({ inventory: "", items: "", lab: "" });
  const [filterSupplier, setFilterSupplier] = useState("");
  const searchQuery = searchQueries[activeTab] ?? "";
  const filterName = filterNames[activeTab] ?? "";
  const setSearchQuery = (value) =>
    setSearchQueries((prev) => ({ ...prev, [activeTab]: value }));
  const setFilterName = (value) =>
    setFilterNames((prev) => ({ ...prev, [activeTab]: value }));
  const [showFilters, setShowFilters] = useState(false);
  const [openOCRDialog, setOpenOCRDialog] = useState(false);
  const [openOCRResultDialog, setOpenOCRResultDialog] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  const [addStockMenuAnchor, setAddStockMenuAnchor] = useState(null);
  
  // Expandable rows states
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  const [expandedLabTests, setExpandedLabTests] = useState(new Set());
  const [labTestDetailsById, setLabTestDetailsById] = useState({});
  const [loadingLabTestDetails, setLoadingLabTestDetails] = useState({});
  const [sampleCollections, setSampleCollections] = useState([]);
  // Inventory inline cell edit (disabled for now — restore with handlers below)
  // const [editingCell, setEditingCell] = useState(null);
  // const [editingValue, setEditingValue] = useState("");
  // const [originalValue, setOriginalValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [bomData, setBomData] = useState([]); // Store BOM data for name updates
  
  // Edit name dialog state (Action column edit icon)
  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");

  useEffect(() => {
    // Wait for organizationId to be available before fetching for organization users
    const organizationId = getOrganizationId(user);
    const hasOrgId = organizationId != null && organizationId > 0;
    
    // For organization users, wait for organizationId before fetching
    // For common users (no organizationId), proceed immediately
    if (hasOrgId) {
      // OrganizationId is available - fetch immediately
      fetchProductList();
      fetchBomsList();
      fetchLabTestsList();
    } else if (user && user?.roleName !== 'patient') {
      // Organization user but organizationId not yet available - wait for it
      console.log('[PurchaseInvoice] Organization user detected but organizationId not yet available, waiting...');
      
      const handleOrganizationIdUpdate = (event) => {
        const { updatedUser } = event.detail;
        const orgId = updatedUser?.organizationId || getOrganizationId(updatedUser);
        
        if (orgId && Number(orgId) > 0) {
          console.log(`[PurchaseInvoice] OrganizationId updated event received (${orgId}), fetching data immediately`);
          setTimeout(() => {
            fetchProductList();
            fetchBomsList();
            fetchLabTestsList();
          }, 100);
        }
      };

      window.addEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);

      // Also set a timeout as fallback
      const timeout = setTimeout(() => {
        const recheckOrgId = getOrganizationId(user);
        if (recheckOrgId && recheckOrgId > 0) {
          console.log(`[PurchaseInvoice] OrganizationId now available (${recheckOrgId}), fetching data`);
          fetchProductList();
          fetchBomsList();
          fetchLabTestsList();
        } else {
          // Still no organizationId - might be a timing issue, try one more time
          const retryTimeout = setTimeout(() => {
            const finalCheck = getOrganizationId(user);
            if (finalCheck && finalCheck > 0) {
              console.log(`[PurchaseInvoice] OrganizationId found on retry (${finalCheck}), fetching data`);
              fetchProductList();
              fetchBomsList();
              fetchLabTestsList();
            } else {
              console.warn('[PurchaseInvoice] OrganizationId not found after retries. apiClient will use localStorage value.');
              // Still fetch - apiClient will use whatever organizationId is in localStorage
              fetchProductList();
              fetchBomsList();
              fetchLabTestsList();
            }
          }, 500);
          return () => clearTimeout(retryTimeout);
        }
      }, 300);

      return () => {
        clearTimeout(timeout);
        window.removeEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);
      };
    } else if (user) {
      // User available but no organizationId - common user or patient, proceed
      fetchProductList();
      fetchBomsList();
      fetchLabTestsList();
    }
  }, [user, user?.organizationId]);

  useEffect(() => {
    if (expiryThresholdLoading) return;
    setProductList((prev) => {
      if (!prev?.length) return prev;
      return applyExpiryFlagsToGroupedProducts(prev, expiryThresholdMonths);
    });
  }, [expiryThresholdMonths, expiryThresholdLoading]);

  const isExpired = (expiryDate) => isExpiredExpiryDate(expiryDate);

  const getPathStockThreshold = (groupOrRecord) => {
    const critical = Number(groupOrRecord?.criticalLevel);
    if (Number.isFinite(critical) && critical > 0) return critical;
    return readPathLowStockQuantityThreshold(
      getOrganizationId(user) || user?.organizationId
    );
  };

  const getNameColor = (group) =>
    getInventoryNameColor({
      expiryStatus: group?.worstExpiryStatus,
      stockStatus: getStockStatus(
        group?.totalQuantity,
        getPathStockThreshold(group)
      ),
    });

  useEffect(() => {
    // Reset pagination on tab switch
    setPage(0);
    if (activeTab === 'lab') {
      if (!fetching.lab_tests && lab_tests.length === 0) fetchLabTestsList();
      if (bomList.length === 0) fetchBomsList();
      if (sampleCollections.length === 0) fetchSampleCollectionsList();
    } else if (activeTab === 'items') {
      if (bomList.length === 0) fetchBomsList();
    } else if (activeTab === 'inventory' || activeTab === 'transactionHistory') {
      if (!fetching.product_list && product_list.length === 0) fetchProductList();
      if (bomList.length === 0) fetchBomsList();
      if (!fetching.lab_tests && lab_tests.length === 0) fetchLabTestsList();
    }
  }, [activeTab]);

  useEffect(() => {
    const pathTab = String(searchParams.get("pathTab") || "").trim().toLowerCase();
    if (pathTab !== "lab") return;
    setActiveTab("lab");
    const labTestId = String(searchParams.get("labTestId") || "").trim();
    const labTestName = String(searchParams.get("labTestName") || "").trim();
    if (labTestId || labTestName) {
      setSearchQueries((prev) => ({ ...prev, lab: labTestId || labTestName }));
      setPage(0);
    }
  }, [searchParams]);

  // const fetchProductList = async () => {
  //   setFetching({ ...fetching, product_list: true });
  //   try {
  //     const data = await getAllInventory();

  //     setProductList(data);
  //   } catch (error) {
  //     console.log(error);
  //   }
  //   setFetching({ ...fetching, product_list: false });
  // };

  const fetchProductList = async () => {
    setFetching((prev) => ({ ...prev, product_list: true }));
    try {
      let inventoryData = await getAllInventory();
      const invoiceData = await getInvoiceList({ fullRange: true }).catch(() => []);
      // Fetch BOMs from base table (not organization-specific) for reference
      let bomData = await getBOMsFromBaseTable();
      
      // Defensive filtering: Filter inventory by organizationId if user has one
      // BOMs are from base table, so no filtering needed
      const organizationId = getOrganizationId(user);
      if (organizationId && organizationId > 0) {
        console.log(`[PurchaseInvoice] Filtering inventory data for organizationId: ${organizationId}`);
        inventoryData = filterByOrganization(inventoryData, user);
      }
      
      // Store BOM data for later use
      setBomData(bomData);
      
      // Create a map for BOM details
      const bomMap = (bomData || []).reduce((acc, bom) => {
        acc[bom.id] = bom;
        return acc;
      }, {});
      const invoices = Array.isArray(invoiceData) ? invoiceData : [];
      const invoiceMetaById = invoices.reduce((acc, invoice) => {
        const id = invoice?.id ?? invoice?.invoiceId;
        if (id == null) return acc;
        const supplierName =
          invoice?.supplierName ||
          invoice?.supplier_name ||
          invoice?.fromName ||
          invoice?.from_name ||
          "";
        const invoiceNumber = String(
          invoice?.invoiceNumber || invoice?.invoice_number || ""
        ).trim();
        acc[String(id)] = { supplierName, invoiceNumber };
        return acc;
      }, {});
      
      // Group by name for display, but keep individual records
      const groupedData = inventoryData.reduce((acc, current) => {
        const key = current.name;
        const expiryDateValue =
          current.expirationDate || current.expiryDate || current.expiry;
        const expired = isExpired(expiryDateValue);
        const expiryStatus = getExpiryStatus(expiryDateValue);
        const invoiceId = current.invoiceId ?? current.invoice_id;
        const invoiceMeta =
          invoiceId != null ? invoiceMetaById[String(invoiceId)] : null;
        const invoiceSupplierName =
          current.supplierName ||
          current.supplier_name ||
          current.fromName ||
          current.from_name ||
          invoiceMeta?.supplierName ||
          "";
        const invoiceNumber =
          String(current.invoiceNumber || current.invoice_number || "").trim() ||
          invoiceMeta?.invoiceNumber ||
          "";
        const criticalLevel = resolvePathCriticalLevel(
          current.criticalLevel ?? current.critical_level
        );
        const quantity = Number(current.quantity ?? current.qty ?? 0) || 0;
        const recordWithMeta = {
          ...current,
          supplierName: invoiceSupplierName,
          invoiceNumber,
          criticalLevel,
          quantity,
        };

        if (!acc[key]) {
          acc[key] = {
            groupKey: key,
            id: current.id,
            name: key,
            totalQuantity: expired ? 0 : quantity,
            records: [recordWithMeta],
            bomId: current.bomId,
            price: current.price || 0,
            criticalLevel,
            bomName: bomMap[current.bomId]?.name || "",
            supplierName: invoiceSupplierName,
            hasExpiredStock: expired,
            hasNearingExpiry: expiryStatus === "nearing",
            hasGoodStock: expiryStatus === "good",
            worstExpiryStatus: expiryStatus,
          };
        } else {
          acc[key].totalQuantity += expired ? 0 : quantity;
          acc[key].records.push(recordWithMeta);
          if (!acc[key].supplierName && invoiceSupplierName) {
            acc[key].supplierName = invoiceSupplierName;
          }
          acc[key].hasExpiredStock = acc[key].hasExpiredStock || expired;
          acc[key].hasNearingExpiry =
            acc[key].hasNearingExpiry || expiryStatus === "nearing";
          acc[key].hasGoodStock = acc[key].hasGoodStock || expiryStatus === "good";
          if (expiryStatus === "expired") {
            acc[key].worstExpiryStatus = "expired";
          } else if (
            expiryStatus === "nearing" &&
            acc[key].worstExpiryStatus !== "expired"
          ) {
            acc[key].worstExpiryStatus = "nearing";
          } else if (
            expiryStatus === "good" &&
            acc[key].worstExpiryStatus !== "expired" &&
            acc[key].worstExpiryStatus !== "nearing"
          ) {
            acc[key].worstExpiryStatus = "good";
          }
        }
        return acc;
      }, {});

      const result = Object.values(groupedData);
      const sorted = result.sort((a, b) => b.id - a.id);
      const thresholdMonths =
        readPathExpiryThresholdMonthsFromStorage(organizationId) ??
        expiryThresholdMonths;
      setProductList(applyExpiryFlagsToGroupedProducts(sorted, thresholdMonths));
    } catch (error) {
      console.log(error);
    }
    setFetching((prev) => ({ ...prev, product_list: false }));
  };

  const fetchLabTestsList = async () => {
    setFetching((prev)=>({ ...prev, lab_tests: true }));
    try {
      // Fetch lab tests from base table (not organization-specific)
      let tests = await getAllLabTests();
      
      const sorted = [...(tests || [])].sort((a,b)=> (b.id ?? 0) - (a.id ?? 0));
      setLabTests(sorted);
      // Drop cached per-test details so Samples/Consumables columns reflect latest links
      setLabTestDetailsById({});
      setLoadingLabTestDetails({});
    } catch (e) {
      setLabTests([]);
    }
    setFetching((prev)=>({ ...prev, lab_tests: false }));
  };

  // Fetch path items (BOMs)
  const fetchBomsList = async () => {
    try {
      // Fetch BOMs from base table (not organization-specific)
      let list = await getBOMsFromBaseTable();
      
      const sorted = [...(list || [])].sort((a,b)=> (b.id ?? 0) - (a.id ?? 0));
      setBomList(sorted);
    } catch (e) {
      setBomList([]);
    }
  };

  const fetchSampleCollectionsList = async () => {
    try {
      const list = await getSampleCollectionsFromBaseTable();
      setSampleCollections(Array.isArray(list) ? list : []);
    } catch (e) {
      setSampleCollections([]);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filteredRows = product_list.filter((p) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const batchNumbers = p.records
        ? p.records.map((r) => String(r.batchNumber || "")).filter(Boolean)
        : [];
      const vals = [
        p.id,
        p.name,
        p.supplierName,
        p.price,
        p.totalQuantity,
        p.criticalLevel,
        ...batchNumbers,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (filterName.trim()) {
      if (!String(p.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
    }
    if (filterSupplier.trim()) {
      if (
        !String(p.supplierName || "")
          .toLowerCase()
          .includes(filterSupplier.toLowerCase())
      ) {
        return false;
      }
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterSupplier("");
  };

  const handleExport = async () => {
    if (activeTab === "items" || activeTab === "lab") {
      const isItemsTab = activeTab === "items";
      const rows = (isItemsTab ? bomList : lab_tests).filter((row) => {
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const values = isItemsTab
            ? [row.id, row.name, row.unitType, row.requiredQuantity, row.cost]
            : [row.id, row.name, row.amount];
          const matchesSearch = values
            .filter((value) => value != null)
            .map((value) => String(value).toLowerCase())
            .some((value) => value.includes(query));
          if (!matchesSearch) return false;
        }
        if (
          filterName.trim() &&
          !String(row.name || "")
            .toLowerCase()
            .includes(filterName.toLowerCase())
        ) {
          return false;
        }
        return true;
      });
      const headers = isItemsTab
        ? ["Id", "Name", "Unit Type", "Req. Qty", "Cost"]
        : ["Test Id", "Name", "Amount", "Sample Details", "Consumables Details"];
      const tableRows = rows.map((row) => {
        if (isItemsTab) {
          return [row.id, row.name, row.unitType, row.requiredQuantity, row.cost];
        }
        const detailSource = labTestDetailsById[String(row.id)] || row;
        return [
          row.id,
          row.name,
          row.amount,
          formatLabTestLinkSummary(resolveLabTestSampleDetails(detailSource, samplesById)) || "-",
          formatLabTestLinkSummary(
            resolveLabTestConsumableDetails(detailSource, bomsById)
          ) || "-",
        ];
      });

      await downloadStyledTableExcel({
        filename: `${isItemsTab ? "path_items" : "lab_tests"}_${dayjs().format("YYYY-MM-DD")}`,
        sheetName: isItemsTab ? "Path Items" : "Lab Tests",
        title: isItemsTab ? "Path Items" : "Lab Tests",
        organizationName: getOrganizationName(user),
        tableHeaders: headers,
        tableRows,
      });
      return;
    }

    const rows = filteredRows || [];
    const { headers, tableRows } = buildPathInventoryExportTable(rows);
    const inTransactions = buildPathInventoryTransactionsFromRecords(rows);

    await downloadPathInventoryTableExcel({
      filename: `path_inventory_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: PATHOLOGY_INVENTORY_LABEL,
      organizationName: getOrganizationName(user),
      summaryTitle: `${PATHOLOGY_INVENTORY_LABEL} Summary`,
      periodLabel: "All time",
      summary: buildPathInventoryExportSummary({
        inventoryRows: rows,
        transactions: inTransactions,
      }),
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleAddStockClick = (event) => {
    setAddStockMenuAnchor(event.currentTarget);
  };

  const handleAddStockMenuClose = () => {
    setAddStockMenuAnchor(null);
  };

  const handleManualAddStock = () => {
    setOpenProductModal(true);
    handleAddStockMenuClose();
  };

  const handleUploadInvoice = () => {
    setOpenOCRDialog(true);
    handleAddStockMenuClose();
  };

  const handleOCRComplete = (data) => {
    setOcrData(data);
    setOpenOCRResultDialog(true);
  };

  const handleOCRSuccess = async (result) => {
    await fetchProductList();
    setOcrData(null);
  };

  // Expandable rows functions
  const toggleRowExpansion = (groupKey) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(groupKey)) {
      newExpandedRows.delete(groupKey);
    } else {
      newExpandedRows.add(groupKey);
    }
    setExpandedRows(newExpandedRows);
  };

  const samplesById = React.useMemo(
    () =>
      new Map(
        (sampleCollections || [])
          .filter((sample) => sample?.id != null)
          .map((sample) => [Number(sample.id), sample])
      ),
    [sampleCollections]
  );

  const bomsById = React.useMemo(
    () =>
      new Map(
        (bomList.length ? bomList : bomData || [])
          .filter((bom) => bom?.id != null)
          .map((bom) => [Number(bom.id), bom])
      ),
    [bomList, bomData]
  );

  const labTestDetailsRef = React.useRef(labTestDetailsById);
  labTestDetailsRef.current = labTestDetailsById;
  const loadingLabTestDetailsRef = React.useRef(loadingLabTestDetails);
  loadingLabTestDetailsRef.current = loadingLabTestDetails;

  const ensureLabTestDetails = async (labTestId) => {
    const key = String(labTestId);
    if (!key || labTestDetailsRef.current[key] || loadingLabTestDetailsRef.current[key]) {
      return;
    }
    loadingLabTestDetailsRef.current = {
      ...loadingLabTestDetailsRef.current,
      [key]: true,
    };
    setLoadingLabTestDetails((prev) => ({ ...prev, [key]: true }));
    try {
      const full = await getLabTestByIdFromBaseTable(labTestId);
      if (full) {
        labTestDetailsRef.current = { ...labTestDetailsRef.current, [key]: full };
        setLabTestDetailsById((prev) => ({ ...prev, [key]: full }));
      }
    } catch (error) {
      console.warn("[PurchaseInvoice] Could not load lab test details:", error);
    } finally {
      loadingLabTestDetailsRef.current = {
        ...loadingLabTestDetailsRef.current,
        [key]: false,
      };
      setLoadingLabTestDetails((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleLabTestExpansion = async (labTestId) => {
    const key = String(labTestId);
    const next = new Set(expandedLabTests);
    if (next.has(key)) {
      next.delete(key);
      setExpandedLabTests(next);
      return;
    }
    next.add(key);
    setExpandedLabTests(next);
    await ensureLabTestDetails(labTestId);
  };

  const renderLabTestLinkColumn = (rows = []) => {
    if (!rows.length) return "-";
    return (
      <Box sx={{ fontSize: "0.8125rem", lineHeight: 1.4 }}>
        {rows.map((row) => (
          <Box key={row.id}>{formatLabTestLinkLine(row)}</Box>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    if (activeTab !== "lab") return;
    const filtered = (lab_tests || []).filter((t) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const detailSource = labTestDetailsById[String(t.id)] || t;
        const sampleNames = resolveLabTestSampleDetails(detailSource, samplesById).map(
          (item) => item.name
        );
        const consumableNames = resolveLabTestConsumableDetails(
          detailSource,
          bomsById
        ).map((item) => item.name);
        const vals = [t.id, t.name, t.amount, ...sampleNames, ...consumableNames]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        if (!vals.some((v) => v.includes(q))) return false;
      }
      if (filterName.trim()) {
        if (!String(t.name || "").toLowerCase().includes(filterName.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
    const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    paged.forEach((row) => {
      if (row?.id != null) ensureLabTestDetails(row.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, lab_tests, page, rowsPerPage, searchQuery, filterName]);

  // Inventory fields are read-only for now. Restore the commented handlers below to re-enable.
  const renderEditableCell = (record, fieldName, value) => {
    let displayValue = "-";
    if (value != null && value !== "") {
      if (fieldName === "expirationDate") {
        displayValue = dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : value;
      } else {
        displayValue = value;
      }
    }
    return <Box component="span">{displayValue}</Box>;
  };

  /* DISABLED: individual inventory field editing (restore when needed)
  // Individual cell editing functions
  const handleCellClick = (inventoryId, columnId, currentValue) => {
    if (editingCell) return; // Prevent multiple edits
    
    setEditingCell({ inventoryId, columnId });
    // Handle null/undefined values - convert to empty string for editing
    const valueToEdit = currentValue != null ? String(currentValue) : "";
    setEditingValue(valueToEdit);
    setOriginalValue(valueToEdit);
  };

  const handleCellChange = (event) => {
    setEditingValue(event.target.value);
  };

  const handleCellSave = async () => {
    if (!editingCell) return;
    
    setSaving(true);
    try {
      const { inventoryId, columnId } = editingCell;
      
      // Find the specific inventory record
      let targetRecord = null;
      let groupIndex = -1;
      let recordIndex = -1;
      
      for (let i = 0; i < product_list.length; i++) {
        const group = product_list[i];
        for (let j = 0; j < group.records.length; j++) {
          if (group.records[j].id === inventoryId) {
            targetRecord = group.records[j];
            groupIndex = i;
            recordIndex = j;
            break;
          }
        }
        if (targetRecord) break;
      }
      
      if (!targetRecord) {
        throw new Error("Record not found");
      }

      if (columnId === "name") {
        const trimmedName = String(editingValue || "").trim();
        if (!trimmedName) {
          alert("Path item name is required.");
          return;
        }

        let pathItems = bomData;
        try {
          const fresh = await getBOMsFromBaseTable();
          pathItems = Array.isArray(fresh) ? fresh : [];
        } catch (error) {
          console.warn("[PurchaseInvoiceTable] Could not refresh path items for duplicate check:", error);
        }

        const duplicateError = validateUniqueCatalogName({
          name: trimmedName,
          items: pathItems,
          excludeId: targetRecord.bomId,
          getName: getPathItemDisplayName,
          getId: (item) => item?.id,
          entityLabel: "Path item",
        });
        if (duplicateError) {
          alert(duplicateError);
          return;
        }
      }

      // Update only the specific field in the specific record
      const updatedProductList = [...product_list];
      let localUpdatedValue = editingValue;
      
      // Handle different field types for local state
      if (columnId === 'quantity') {
        localUpdatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'price') {
        localUpdatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'criticalLevel') {
        localUpdatedValue = parseInt(editingValue) || 0;
      } else if (columnId === 'expirationDate') {
        localUpdatedValue = editingValue ? editingValue : null;
      } else if (columnId === 'batchNumber') {
        localUpdatedValue = editingValue === 'N/A' ? null : editingValue;
      }
      
      updatedProductList[groupIndex].records[recordIndex] = {
        ...updatedProductList[groupIndex].records[recordIndex],
        [columnId]: localUpdatedValue
      };
      
      if (columnId === "quantity" || columnId === "expirationDate") {
        updatedProductList[groupIndex].totalQuantity =
          updatedProductList[groupIndex].records.reduce((sum, record) => {
            const expiryDateValue =
              record.expirationDate || record.expiryDate || record.expiry;
            const expired = isExpired(expiryDateValue);
            return sum + (expired ? 0 : parseFloat(record.quantity) || 0);
          }, 0);

        let hasExpiredStock = false;
        let hasNearingExpiry = false;
        let hasGoodStock = false;
        let worstStatus = "good";
        updatedProductList[groupIndex].records.forEach((record) => {
          const expiryDateValue =
            record.expirationDate || record.expiryDate || record.expiry;
          const status = getExpiryStatus(expiryDateValue);
          if (status === "expired") {
            hasExpiredStock = true;
            worstStatus = "expired";
          } else if (status === "nearing") {
            hasNearingExpiry = true;
            if (worstStatus !== "expired") worstStatus = "nearing";
          } else if (status === "good") {
            hasGoodStock = true;
            if (worstStatus !== "expired" && worstStatus !== "nearing") {
              worstStatus = "good";
            }
          }
        });
        updatedProductList[groupIndex].hasExpiredStock = hasExpiredStock;
        updatedProductList[groupIndex].hasNearingExpiry = hasNearingExpiry;
        updatedProductList[groupIndex].hasGoodStock = hasGoodStock;
        updatedProductList[groupIndex].worstExpiryStatus = worstStatus;
      }
      
      // Update group name if name was changed
      if (columnId === 'name') {
        updatedProductList[groupIndex].name = localUpdatedValue;
        updatedProductList[groupIndex].groupKey = localUpdatedValue;
      }
      
      setProductList(updatedProductList);

      // Update the backend - send all existing fields to prevent data loss
      let updatedValue = editingValue;
      
      // Handle different field types
      if (columnId === 'quantity') {
        updatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'price') {
        updatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'criticalLevel') {
        updatedValue = parseInt(editingValue) || 0;
      } else if (columnId === 'expirationDate') {
        updatedValue = editingValue ? editingValue : null;
      } else if (columnId === 'batchNumber') {
        updatedValue = editingValue === 'N/A' ? null : editingValue;
      }
      
      // Create update data with proper field mapping
      // Only include fields that should be sent to the backend (exclude date arrays and internal fields)
      // Start with all fields from targetRecord, then clean and update
      const updateData = { ...targetRecord };
      
      // Update the specific field being edited
      updateData[columnId] = updatedValue;
      
      // Remove fields that are arrays (like createDate, updateDate) - these cause issues
      Object.keys(updateData).forEach(key => {
        const value = updateData[key];
        if (Array.isArray(value)) {
          delete updateData[key];
        }
      });
      
      // Format expirationDate if it exists and is not already a string
      if (updateData.expirationDate && typeof updateData.expirationDate !== 'string') {
        try {
          updateData.expirationDate = dayjs(updateData.expirationDate).format("YYYY-MM-DD");
        } catch (e) {
          console.warn("Could not format expirationDate:", e);
          // Keep the original value if formatting fails
        }
      }
      
      // Also handle expiryDate field (some records might use this instead)
      if (updateData.expiryDate && typeof updateData.expiryDate !== 'string') {
        try {
          updateData.expiryDate = dayjs(updateData.expiryDate).format("YYYY-MM-DD");
        } catch (e) {
          console.warn("Could not format expiryDate:", e);
        }
      }
      
      // Remove internal fields that shouldn't be sent (but keep null values for required fields)
      delete updateData.createDate;
      delete updateData.updateDate;
      delete updateData.id; // Don't send ID in body, it's in the URL
      
      console.log(`[PurchaseInvoiceTable] Updating inventory ${inventoryId} with data:`, updateData);
      
      // Update the inventory record (organization-specific table)
      await updateInventory(inventoryId, updateData);

      // If name was updated, also update the lab_bom table (base table)
      if (columnId === 'name' && targetRecord.bomId) {
        try {
          const bomToUpdate = bomData.find(bom => bom.id === targetRecord.bomId);
          if (bomToUpdate) {
            // Only include BOM fields that should be updated (exclude internal/date fields)
            const bomUpdateData = {
              name: updatedValue, // Updated name
              unitType: bomToUpdate.unitType,
              qtyUnit: bomToUpdate.qtyUnit ?? bomToUpdate.qty_unit,
              details: bomToUpdate.details,
              requiredQuantity: bomToUpdate.requiredQuantity,
              cost: bomToUpdate.cost,
              costPerUnit: bomToUpdate.costPerUnit,
              description: bomToUpdate.description,
            };
            // Remove null/undefined values
            Object.keys(bomUpdateData).forEach(key => {
              if (bomUpdateData[key] === null || bomUpdateData[key] === undefined) {
                delete bomUpdateData[key];
              }
            });
            console.log(`[PurchaseInvoiceTable] Updating BOM ${targetRecord.bomId} in base table with data:`, bomUpdateData);
            // Update BOM in base table (not organization-specific)
            await updateBOMInBaseTable(targetRecord.bomId, bomUpdateData);
          }
        } catch (bomError) {
          console.error("Error updating BOM name:", bomError);
          // Don't throw error here as inventory was already updated successfully
        }
      }

      // Clear editing state
      setEditingCell(null);
      setEditingValue("");
      setOriginalValue("");
      
    } catch (error) {
      console.error("Error saving cell:", error);
      alert("Failed to save changes. Please try again.");
      // Refresh data to revert changes
      await fetchProductList();
    } finally {
      setSaving(false);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue("");
    setOriginalValue("");
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCellSave();
    } else if (event.key === 'Escape') {
      handleCellCancel();
    }
  };
  DISABLED inventory field editing */

  // Handler for editing group name (updates both lab_bom and all related inventory records)
  const handleEditPathItemFromInventory = (group) => {
    const bomId = group?.bomId ?? group?.bom_id;
    const groupName = String(group?.name || "").trim().toLowerCase();
    const catalog = [...(bomList || []), ...(bomData || [])];
    const bom =
      (bomId != null && bomId !== ""
        ? catalog.find((item) => String(item?.id) === String(bomId))
        : null) ||
      catalog.find(
        (item) => String(item?.name || "").trim().toLowerCase() === groupName
      ) ||
      null;

    if (!bom) {
      toast.info("Path item was not found for this inventory row");
      return;
    }

    setEditingBom(bom);
    setBomForm({
      name: bom.name || "",
      unitType: bom.unitType || "",
      requiredQuantity: bom.requiredQuantity ?? "",
      cost: bom.cost ?? "",
    });
    setOpenBomModal(true);
  };

  const handleSaveGroupName = async () => {
    if (!editingGroup || !editingGroupName.trim()) {
      alert("Please enter a valid name");
      return;
    }

    setSaving(true);
    try {
      const newName = editingGroupName.trim();
      
      // Step 1: Update the lab_bom table (base table) if bomId exists
      if (editingGroup.bomId) {
        const bomToUpdate = bomData.find(bom => bom.id === editingGroup.bomId);
        if (bomToUpdate) {
          const bomUpdateData = {
            ...bomToUpdate,
            name: newName,
          };
          // Update BOM in base table (not organization-specific)
          await updateBOMInBaseTable(editingGroup.bomId, bomUpdateData);
        }
      }

      // Step 2: Update all inventory records with this name
      const updatePromises = editingGroup.records.map(record => {
        // Start with all fields from record, then clean and update
        const updateData = { ...record };
        
        // Update the name
        updateData.name = newName;
        
        // Remove fields that are arrays (like createDate, updateDate) - these cause issues
        Object.keys(updateData).forEach(key => {
          const value = updateData[key];
          if (Array.isArray(value)) {
            delete updateData[key];
          }
        });
        
        // Format expirationDate if it exists and is not already a string
        if (updateData.expirationDate && typeof updateData.expirationDate !== 'string') {
          try {
            updateData.expirationDate = dayjs(updateData.expirationDate).format("YYYY-MM-DD");
          } catch (e) {
            console.warn("Could not format expirationDate:", e);
          }
        }
        
        // Also handle expiryDate field (some records might use this instead)
        if (updateData.expiryDate && typeof updateData.expiryDate !== 'string') {
          try {
            updateData.expiryDate = dayjs(updateData.expiryDate).format("YYYY-MM-DD");
          } catch (e) {
            console.warn("Could not format expiryDate:", e);
          }
        }
        
        // Remove internal fields that shouldn't be sent (but keep null values for required fields)
        delete updateData.createDate;
        delete updateData.updateDate;
        delete updateData.id; // Don't send ID in body, it's in the URL
        
        console.log(`[PurchaseInvoiceTable] Updating inventory ${record.id} (group name update) with data:`, updateData);
        
        return updateInventory(record.id, updateData);
      });
      
      await Promise.all(updatePromises);

      // Step 3: Refresh the product list to show updated data
      await fetchProductList();

      // Close dialog
      setOpenEditNameDialog(false);
      setEditingGroup(null);
      setEditingGroupName("");
      
      alert("Name updated successfully in both Path Item BOM and inventory records!");
      
    } catch (error) {
      console.error("Error updating group name:", error);
      alert("Failed to update name. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditGroupName = () => {
    setOpenEditNameDialog(false);
    setEditingGroup(null);
    setEditingGroupName("");
  };

  /* DISABLED: old click-to-edit cell renderer (read-only renderer is used above)
  // Helper function to render editable cells
  const renderEditableCell = (record, fieldName, value, isNumeric = false) => {
    const isEditing = editingCell?.inventoryId === record.id && editingCell?.columnId === fieldName;
    
    // Display value - handle null/undefined values and format dates
    let displayValue = '-';
    if (value != null && value !== '') {
      if (fieldName === 'expirationDate') {
        // Format date for display
        displayValue = dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : value;
      } else {
        displayValue = value;
      }
    }
    
    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={editingValue}
            onChange={handleCellChange}
            onKeyDown={handleKeyPress}
            size="small"
            autoFocus
            disabled={saving}
            type={isNumeric ? 'number' : fieldName === 'expirationDate' ? 'date' : 'text'}
            inputProps={isNumeric ? { min: 0 } : {}}
            sx={{ minWidth: 100 }}
            placeholder={fieldName === 'expirationDate' ? 'YYYY-MM-DD' : 'Enter value'}
          />
          <IconButton
            size="small"
            onClick={handleCellSave}
            disabled={saving}
            color="primary"
          >
            {saving ? <CircularProgress size={16} /> : <SaveIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCellCancel}
            disabled={saving}
            color="secondary"
          >
            <CancelIcon />
          </IconButton>
        </Box>
      );
    } else {
      return (
        <Tooltip title={`Click to edit ${fieldName}${value == null || value === '' ? ' (empty)' : ''}`} arrow>
          <Box
            onClick={() => handleCellClick(record.id, fieldName, value)}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: 1,
                px: 1
              },
              color: value == null || value === '' ? 'text.disabled' : 'text.primary',
              fontStyle: value == null || value === '' ? 'italic' : 'normal',
            }}
          >
            {displayValue}
          </Box>
        </Tooltip>
      );
    }
  };
  DISABLED inventory field editing */

  return (
    <Grid container>
      {!hideMainTitle && (
        <Grid item xs={12} mb={0.5}>
          <Header title={PATHOLOGY_INVENTORY_LABEL} />
        </Grid>
      )}

      <Paper sx={{ width: "100%" }}>
        <Box sx={PAGE_TABS_BAR_SX}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            {...PAGE_TAB_LIST_PROPS}
          >
            <Tab value="inventory" label={PATHOLOGY_INVENTORY_LABEL} />
            <Tab value="transactionHistory" label="Transaction History" />
            <Tab value="items" label="Path Items" />
            <Tab value="lab" label="Lab Tests" />
            {/* Show Supplier Details for admin and pathology admin/pathdesk with Pathology Inventory capability */}
            {showAdminPathTabs && (
              <Tab value="suppliers" label="Supplier Details" />
            )}
            {showAdminPathTabs && (
              <Tab value="nearExpirySettings" label="Near-expiry Settings" />
            )}
          </Tabs>
        </Box>

        {(activeTab === "lab" ||
          activeTab === "items" ||
          activeTab === "inventory") && (
        <Box sx={{ ...TABLE_ACTIONS_ROW_SX, px: 1.5, pt: 1, mb: 0 }}>
        {activeTab === 'lab' ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={TABLE_ADD_BUTTON_SX}
            onClick={() => setOpenLabTestModal(true)}
          >
            Create Lab Test
          </Button>
        ) : activeTab === 'items' ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={TABLE_ADD_BUTTON_SX}
            onClick={() => { setEditingBom(null); setBomForm({ name: "", description: "", unitType: "", requiredQuantity: "", cost: "", costPerUnit: "" }); setOpenBomModal(true); }}
          >
            Create Path Item
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={TABLE_ADD_BUTTON_SX}
              onClick={handleAddStockClick}
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={<AddIcon />}
            >
              Add Stock
            </Button>
            <Menu
              anchorEl={addStockMenuAnchor}
              open={Boolean(addStockMenuAnchor)}
              onClose={handleAddStockMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem onClick={handleManualAddStock}>
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Manual</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleUploadInvoice}>
                <ListItemIcon>
                  <CloudUploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Upload Invoice</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
        </Box>
        )}

        {/* Only show search/filter/export UI for tabs other than suppliers / settings */}
        {activeTab !== 'suppliers' &&
          activeTab !== 'nearExpirySettings' &&
          activeTab !== 'transactionHistory' && (
          <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
            <Box sx={TABLE_SEARCH_ROW_SX}>
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
                <IconButton
                  size="small"
                  sx={TABLE_ICON_BUTTON_SX}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  sx={TABLE_ICON_BUTTON_SX}
                  onClick={async () => {
                    if (activeTab === 'lab') {
                      await fetchLabTestsList();
                      if (sampleCollections.length === 0) await fetchSampleCollectionsList();
                      if (bomList.length === 0) await fetchBomsList();
                    } else if (activeTab === 'items') {
                      await fetchBomsList();
                    } else {
                      await fetchProductList();
                    }
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {activeTab === "inventory" && (
              <InventoryStatusLegend sx={{ mt: 0.75 }} />
            )}

            {showFilters && (
            <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
              <TextField size="small" label="Name" value={filterName} onChange={(e)=>setFilterName(e.target.value)} />
              {activeTab === "inventory" && (
                <TextField
                  size="small"
                  label="Supplier"
                  value={filterSupplier}
                  onChange={(e) => setFilterSupplier(e.target.value)}
                  sx={{ minWidth: 140 }}
                />
              )}
              <Tooltip title="Clear filters">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            )}
          </Box>
        )}
        {activeTab === 'inventory' && (
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  if (column.id === "action" && expandedRows.size > 0) {
                    return (
                      <React.Fragment key={`${column.id}-with-batch`}>
                        <StyledTableCell key="batchInfo" style={{ top: 57 }}>
                          Batch Info
                        </StyledTableCell>
                        <StyledTableCell
                          key={column.id}
                          align={column.align}
                          style={{ top: 57, minWidth: column.minWidth }}
                        >
                          {column.label}
                        </StyledTableCell>
                      </React.Fragment>
                    );
                  }
                  return (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{ top: 57, minWidth: column.minWidth }}
                    >
                      {column.label}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {product_list.length === 0 || fetching.product_list ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    {fetching.product_list && <CircularProgress size={40} />}
                    {!fetching.product_list && product_list.length === 0 && (
                      <Alert>No Stock </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((group) => {
                    const isExpanded = expandedRows.has(group.groupKey);
                    return (
                      <React.Fragment key={group.groupKey}>
                        {/* Main group row */}
                        <TableRow hover>
                          {columns.map((column) => {
                            if (column.id === "expand") {
                              return (
                                <TableCell key={column.id} sx={{ width: 50 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(group.groupKey)}
                                  >
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                </TableCell>
                              );
                            } else if (column.id === "action") {
                              return (
                                <React.Fragment key={column.id}>
                                  {expandedRows.size > 0 && <TableCell />}
                                  <TableCell align={column.align}>
                                    <Tooltip title="Edit path item">
                                    <IconButton
                                      sx={{ color: theme.palette.primary.main }}
                                      onClick={() => handleEditPathItemFromInventory(group)}
                                      size="small"
                                    >
                                      <ModeEditOutlineTwoToneIcon />
                                    </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </React.Fragment>
                              );
                            } else if (column.id === "name") {
                              const pathThreshold = getPathStockThreshold(group);
                              const stockStatus = getStockStatus(
                                group.totalQuantity,
                                pathThreshold
                              );
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  <Box
                                    sx={{
                                      color: getNameColor(group),
                                      fontWeight:
                                        group.hasExpiredStock ||
                                        group.hasNearingExpiry ||
                                        stockStatus
                                          ? "bold"
                                          : "normal",
                                      display: "flex",
                                      alignItems: "center",
                                      flexWrap: "wrap",
                                      gap: 0.25,
                                    }}
                                  >
                                    {group.name}
                                    <InventoryStatusBadges
                                      statusKeys={getGroupInventoryBadgeKeys(
                                        group,
                                        pathThreshold
                                      )}
                                    />
                                  </Box>
                                </TableCell>
                              );
                            } else {
                              const value =
                                column.id === "quantity"
                                  ? group.totalQuantity
                                  : group[column.id];
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {column.format && typeof value === "number"
                                    ? column.format(value)
                                    : value}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                        
                        {/* Expanded individual records — plain rows; stock/expiry indicators stay on main row */}
                        {isExpanded &&
                          [...(group.records || [])]
                            .sort(
                              (a, b) => Number(b?.id || 0) - Number(a?.id || 0)
                            )
                            .map((record, index) => {
                            return (
                          <TableRow
                            key={`${group.groupKey}-${record.id || index}`}
                          >
                            <TableCell sx={{ pl: 4, width: 50 }}>
                              <Box sx={{ width: 20, height: 1, backgroundColor: 'grey.300' }} />
                            </TableCell>
                            <TableCell sx={{ pl: 2 }}>
                              {record.id}
                            </TableCell>
                            <TableCell sx={{ pl: 2 }}>
                              {renderEditableCell(record, 'name', record.name)}
                            </TableCell>
                            <TableCell>
                              {renderEditableCell(record, 'price', record.price || 0, true)}
                            </TableCell>
                            <TableCell>
                              {renderEditableCell(record, 'quantity', record.quantity, true)}
                            </TableCell>
                            <TableCell>
                              {renderEditableCell(record, 'criticalLevel', record.criticalLevel, true)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                <Box sx={{ mb: 0.5 }}>
                                  Batch: {renderEditableCell(record, 'batchNumber', record.batchNumber)}
                                </Box>
                                <Box sx={{ mb: 0.5 }}>
                                  Expiry:{" "}
                                  {renderEditableCell(
                                    record,
                                    "expirationDate",
                                    record.expirationDate || record.expiryDate
                                  )}
                                </Box>
                                <Box>
                                  Supplier:{" "}
                                  {record.supplierName || group.supplierName || "N/A"}
                                </Box>
                                <Box sx={{ mt: 0.5 }}>
                                  Invoice No:{" "}
                                  {record.invoiceNumber ||
                                    record.invoice_number ||
                                    "N/A"}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                            );
                          })}
                      </React.Fragment>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        {activeTab === 'items' && (
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Unit Type</StyledTableCell>
                <StyledTableCell>Req. Qty</StyledTableCell>
                <StyledTableCell>Cost</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const listAll = (bomList || []).filter((b) => {
                  if (searchQuery.trim()) {
                    const q = searchQuery.toLowerCase();
                    const vals = [b.id, b.name, b.unitType, b.requiredQuantity, b.cost]
                      .filter(Boolean)
                      .map((v) => String(v).toLowerCase());
                    if (!vals.some((v) => v.includes(q))) return false;
                  }
                  if (filterName.trim()) {
                    if (!String(b.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
                  }
                  return true;
                });
                if (!listAll.length) return (
                  <TableRow><TableCell colSpan={8}><Alert>No Path Items</Alert></TableCell></TableRow>
                );
                const paged = listAll.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
                return paged.map((b) => (
                  <TableRow key={`bom-${b.id}`} hover>
                    <TableCell>{b.id}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell>{b.unitType}</TableCell>
                    <TableCell>{b.requiredQuantity}</TableCell>
                    <TableCell>{b.cost}</TableCell>
                    <TableCell>
                      <IconButton sx={{ color: theme.palette.primary.main }} onClick={() => {
                        setEditingBom(b);
                        setBomForm({
                          name: b.name || "",
                          unitType: b.unitType || "",
                          requiredQuantity: b.requiredQuantity ?? "",
                          cost: b.cost ?? "",
                        });
                        setOpenBomModal(true);
                      }}>
                        <ModeEditOutlineTwoToneIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        {activeTab === 'lab' && (
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell style={{ width: 50 }} />
                <StyledTableCell>Test Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Samples</StyledTableCell>
                <StyledTableCell>Consumables</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (fetching.lab_tests) return (
                  <TableRow><TableCell colSpan={7}><CircularProgress size={40} /></TableCell></TableRow>
                );
                // Filter lab tests by search and name filter
                const all = (lab_tests || []).filter((t) => {
                  if (searchQuery.trim()) {
                    const q = searchQuery.toLowerCase();
                    const detailSource = labTestDetailsById[String(t.id)] || t;
                    const sampleNames = resolveLabTestSampleDetails(detailSource, samplesById)
                      .map((item) => item.name);
                    const consumableNames = resolveLabTestConsumableDetails(detailSource, bomsById)
                      .map((item) => item.name);
                    const vals = [t.id, t.name, t.amount, ...sampleNames, ...consumableNames]
                      .filter(Boolean)
                      .map((v) => String(v).toLowerCase());
                    if (!vals.some((v) => v.includes(q))) return false;
                  }
                  if (filterName.trim()) {
                    const q = filterName.toLowerCase();
                    const nameMatch = String(t.name || "").toLowerCase().includes(q);
                    const idMatch = String(t.id || "").toLowerCase().includes(q);
                    if (!nameMatch && !idMatch) return false;
                  }
                  return true;
                });
                const paged = all.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
                if (!all.length) return (
                  <TableRow><TableCell colSpan={7}><Alert>No Lab Tests</Alert></TableCell></TableRow>
                );
                return paged.map((row) => {
                  const rowKey = String(row.id);
                  const isExpanded = expandedLabTests.has(rowKey);
                  const detailSource = labTestDetailsById[rowKey] || row;
                  return (
                  <React.Fragment key={`lab-${row.id}`}>
                  <TableRow hover>
                    <TableCell sx={{ width: 50 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleLabTestExpansion(row.id)}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {renderLabTestLinkColumn(
                        resolveLabTestSampleDetails(detailSource, samplesById)
                      )}
                    </TableCell>
                    <TableCell>
                      {renderLabTestLinkColumn(
                        resolveLabTestConsumableDetails(detailSource, bomsById)
                      )}
                    </TableCell>
                    <TableCell>{row.amount ?? '-'}</TableCell>
                    <TableCell>
                      <IconButton sx={{ color: theme.palette.primary.main }} onClick={async () => {
                        try {
                          // Fetch lab test from base table (not organization-specific)
                          const full = await getLabTestByIdFromBaseTable(row.id);
                          const sampleEntries = parseLabTestSampleEntries(full).map((se) => ({
                            sample_id: se.sample_id,
                            requiredQuantity: se.requiredQuantity,
                          }));
                          const bomEntries = parseLabTestConsumableEntries(full).map((be) => ({
                            bom_id: be.bom_id,
                            requiredQuantity: be.requiredQuantity,
                          }));
                          
                          // Extract subCategories from various possible field names
                          // Backend might return: subCategories, labTestSubCategories, lab_test_sub_categories, or sub_categories
                          // Also handle JSON string format
                          let subCategories = [];
                          let rawSubCategories = null;
                          
                          // Try different field names
                          if (Array.isArray(full.subCategories) && full.subCategories.length > 0) {
                            rawSubCategories = full.subCategories;
                          } else if (Array.isArray(full.labTestSubCategories) && full.labTestSubCategories.length > 0) {
                            rawSubCategories = full.labTestSubCategories;
                          } else if (Array.isArray(full.lab_test_sub_categories) && full.lab_test_sub_categories.length > 0) {
                            rawSubCategories = full.lab_test_sub_categories;
                          } else if (Array.isArray(full.sub_categories) && full.sub_categories.length > 0) {
                            rawSubCategories = full.sub_categories;
                          } else if (typeof full.subCategories === 'string') {
                            // Try parsing as JSON string
                            try {
                              const parsed = JSON.parse(full.subCategories);
                              if (Array.isArray(parsed) && parsed.length > 0) {
                                rawSubCategories = parsed;
                              }
                            } catch (e) {
                              console.warn("Failed to parse subCategories as JSON:", e);
                            }
                          }
                          
                          // Ensure subCategories is properly formatted array
                          if (rawSubCategories && Array.isArray(rawSubCategories)) {
                            subCategories = rawSubCategories.map(sub => ({
                              name: sub.name || sub.subCategoryName || '',
                              referenceValue: sub.referenceValue || sub.reference_value || '',
                              unit: sub.unit || '',
                              category: sub.category || null
                            })).filter(sub => sub.name); // Filter out empty entries
                          }
                          
                          setProductInfo({ 
                            id: full.id, 
                            name: full.name, 
                            category: full.category || full.testType || null, // Include category (fallback to testType if category not available)
                            referenceValue: full.referenceValue, 
                            amount: full.amount, 
                            testType: full.testType || null, // Include testType
                            sampleEntries, 
                            bomEntries,
                            subCategories: subCategories // Include subCategories for prefilling
                          });
                        } catch(e) { 
                          console.error("Error fetching lab test details:", e);
                          setProductInfo(row); 
                        }
                        setOpenLabTestModal(true);
                      }}>
                        <ModeEditOutlineTwoToneIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "grey.50", py: 1.5, px: 2 }}>
                        <LabTestExpandedDetails
                          loading={
                            Boolean(loadingLabTestDetails[rowKey]) &&
                            !resolveLabTestSampleDetails(detailSource, samplesById).length &&
                            !resolveLabTestConsumableDetails(detailSource, bomsById).length
                          }
                          samples={resolveLabTestSampleDetails(detailSource, samplesById)}
                          consumables={resolveLabTestConsumableDetails(detailSource, bomsById)}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        {activeTab === 'transactionHistory' && (
          <Box>
            <PathTransactionHistory
              inventoryGroups={product_list}
              boms={bomList.length ? bomList : bomData}
              labTests={lab_tests}
              onViewInventoryBatch={({ itemName, batchNumber, inventoryId, bomId }) => {
                const name = String(itemName || "").trim();
                const batch = String(batchNumber || "").trim();
                const matchedGroup = findPathInventoryGroupForTransaction(product_list, {
                  itemName: name,
                  batchNumber: batch,
                  inventoryId,
                  bomId,
                });
                clearFilters();
                setActiveTab("inventory");
                setFilterName(matchedGroup?.name || name);
                setShowFilters(Boolean(matchedGroup));
                setPage(0);
                setExpandedRows(
                  new Set(
                    [matchedGroup?.groupKey || matchedGroup?.name].filter(Boolean)
                  )
                );
                if (!matchedGroup) {
                  toast.info(
                    "No inventory stock found for this item. It may have been fully used or removed."
                  );
                }
              }}
              onViewLabTest={({ labTestId, labTestName }) => {
                const id = labTestId != null && labTestId !== "" ? String(labTestId).trim() : "";
                const name = String(labTestName || "").trim();
                setActiveTab("lab");
                setSearchQueries((prev) => ({ ...prev, lab: id || name }));
                setFilterNames((prev) => ({ ...prev, lab: "" }));
                setShowFilters(Boolean(id || name));
                setPage(0);
              }}
            />
          </Box>
        )}
        {/* Tab Panel for Supplier Details */}
        {activeTab === 'suppliers' && showAdminPathTabs && (
          <Box>
            <SupplierDetailsTable inventoryScope="pathology" />
          </Box>
        )}
        {activeTab === "nearExpirySettings" && showAdminPathTabs && (
          <Box sx={{ p: 1.5, pt: 1 }}>
            <PathExpiryThresholdSettings />
          </Box>
        )}
        {activeTab !== "transactionHistory" && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={activeTab === 'lab' ? lab_tests.filter((t)=>{
            if (searchQuery.trim()) {
              const q = searchQuery.toLowerCase();
              const detailSource = labTestDetailsById[String(t.id)] || t;
              const sampleNames = resolveLabTestSampleDetails(detailSource, samplesById).map((item) => item.name);
              const consumableNames = resolveLabTestConsumableDetails(detailSource, bomsById).map((item) => item.name);
              const vals = [t.id, t.name, t.amount, ...sampleNames, ...consumableNames].filter(Boolean).map(v=>String(v).toLowerCase());
              if (!vals.some(v=>v.includes(q))) return false;
            }
            if (filterName.trim()) {
              const q = filterName.toLowerCase();
              const nameMatch = String(t.name || "").toLowerCase().includes(q);
              const idMatch = String(t.id || "").toLowerCase().includes(q);
              if (!nameMatch && !idMatch) return false;
            }
            return true;
          }).length : activeTab === 'items' ? bomList.filter((b)=>{
            if (searchQuery.trim()) {
              const q = searchQuery.toLowerCase();
              const vals = [b.id, b.name, b.unitType, b.requiredQuantity, b.cost]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase());
              if (!vals.some((v) => v.includes(q))) return false;
            }
            if (filterName.trim()) {
              if (!String(b.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
            }
            return true;
          }).length : filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        )}
      </Paper>
      <AddNewPurchaseInvoice
        open={open_product_modal}
        onClose={async () => {
          setOpenProductModal(false);
          setProductInfo(null);
          await fetchProductList();
        }}
        product_info={product_info}
        refreshData={fetchBomsList}
      />
      <CreateNewLabTest
        open={open_Lab_test_modal}
        onClose={async (didChange = false) => {
          const editedId = product_info?.id;
          setOpenLabTestModal(false);
          setProductInfo(null);
          if (!didChange) return;
          if (editedId != null) {
            setLabTestDetailsById((prev) => {
              const next = { ...prev };
              delete next[String(editedId)];
              return next;
            });
          }
          await fetchLabTestsList();
          // Re-load details for rows that stay expanded so Samples/Consumables stay accurate
          Array.from(expandedLabTests).forEach((id) => {
            ensureLabTestDetails(id);
          });
        }}
        product_info={product_info}
      />
      {/* Create / Edit Path Item (BOM) */}
      <CreateNewPathItem
        open={open_bom_modal}
        editingBom={editingBom}
        refreshData={fetchBomsList}
        onClose={async (didChange)=>{
          setOpenBomModal(false);
          setEditingBom(null);
          if (didChange) {
            await fetchBomsList();
            await fetchProductList();
          }
        }}
      />
      
      {/* OCR Dialog for Pathology Inventory */}
      <PathOCRDialog
        open={openOCRDialog}
        onClose={() => setOpenOCRDialog(false)}
        onOCRComplete={handleOCRComplete}
      />
      
      {/* OCR Result Dialog for Pathology Inventory */}
      <PathOCRResultDialog
        open={openOCRResultDialog}
        onClose={() => {
          setOpenOCRResultDialog(false);
          setOcrData(null);
        }}
        ocrData={ocrData}
        onSuccess={handleOCRSuccess}
      />

      <Dialog 
        open={openEditNameDialog} 
        onClose={handleCancelEditGroupName}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Item Name
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will update the name in both the Path Item BOM table and all related inventory records.
            </Typography>
            <TextField
              fullWidth
              label="Item Name"
              value={editingGroupName}
              onChange={(e) => setEditingGroupName(e.target.value)}
              autoFocus
              disabled={saving}
            />
            {editingGroup && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Path Item BOM ID: {editingGroup.bomId || 'N/A'}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Affected Records: {editingGroup.records?.length || 0}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelEditGroupName} 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveGroupName} 
            variant="contained" 
            disabled={saving || !editingGroupName.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
